import moment from "moment";
import { formatCurrencyBRL } from "../../utils/utils";

/**
 * Vocabulário compartilhado pela tela de Clientes (tabela, filtros, KPIs e
 * painel de detalhes). Manter em um único lugar evita que badge, filtro e
 * exportação contem a mesma coisa de formas diferentes.
 */

export const STATUS_OPTIONS = [
  { value: "", label: "Todas as situações" },
  { value: "ACTIVE", label: "Ativos" },
  { value: "EXPIRING", label: "Expirando (2 dias)" },
  { value: "EXPIRED", label: "Expirados" },
];

export const HEALTH_OPTIONS = [
  { value: "", label: "Todos os engajamentos" },
  { value: "ACTIVE", label: "Usando o sistema" },
  { value: "AT_RISK", label: "Em risco (parou de usar)" },
  { value: "NEVER_USED", label: "Nunca usou" },
];

export const HEALTH_BAND_OPTIONS = [
  { value: "", label: "Qualquer saúde" },
  { value: "GOOD", label: "Saudável" },
  { value: "ATTENTION", label: "Atenção" },
  { value: "RISK", label: "Crítica" },
];

export const NO_CONTACT_OPTIONS = [
  { value: "", label: "Tanto faz" },
  { value: "7", label: "Há 7 dias ou mais" },
  { value: "15", label: "Há 15 dias ou mais" },
  { value: "30", label: "Há 30 dias ou mais" },
  { value: "60", label: "Há 60 dias ou mais" },
  { value: "90", label: "Há 90 dias ou mais" },
];

export const PLAN_OPTIONS = [
  { value: "", label: "Todos os planos" },
  { value: "DIAMOND", label: "Diamond" },
  { value: "PRO", label: "Pro" },
  { value: "ESSENTIAL", label: "Essential" },
  { value: "FREE", label: "Free" },
];

export const PAGE_SIZE_OPTIONS = [10, 16, 25, 50, 100];

export const SORT_OPTIONS = [
  { value: "createdAt:desc", label: "Cadastro (mais recente)" },
  { value: "createdAt:asc", label: "Cadastro (mais antigo)" },
  { value: "planExpirationDate:asc", label: "Expiração (mais próxima)" },
  { value: "planExpirationDate:desc", label: "Expiração (mais distante)" },
  { value: "name:asc", label: "Nome (A-Z)" },
  { value: "name:desc", label: "Nome (Z-A)" },
  { value: "appointmentsLast30Days:desc", label: "Mais ativos (30 dias)" },
  { value: "appointmentCount:desc", label: "Mais agendamentos (total)" },
  { value: "professionalCount:desc", label: "Mais profissionais" },
  { value: "totalPaid:desc", label: "Maior valor pago" },
  { value: "lastActivityAt:asc", label: "Parados há mais tempo" },
  { value: "healthScore:asc", label: "Pior saúde primeiro" },
  { value: "monthlyValue:desc", label: "Maior mensalidade" },
  { value: "lastAccessAt:desc", label: "Abriram o app há menos tempo" },
];

export const STATUS_META = {
  ACTIVE: { label: "Ativo", color: "success", dot: "bg-emerald-500" },
  EXPIRING: { label: "Expirando", color: "warning", dot: "bg-amber-500" },
  EXPIRED: { label: "Expirado", color: "failure", dot: "bg-rose-500" },
};

export const HEALTH_META = {
  ACTIVE: {
    label: "Usando",
    color: "success",
    hint: "Registrou agendamentos ou comandas nos últimos 30 dias",
  },
  AT_RISK: {
    label: "Em risco",
    color: "warning",
    hint: "Já usou o sistema, mas está parado há mais de 30 dias",
  },
  NEVER_USED: {
    label: "Nunca usou",
    color: "gray",
    hint: "Nenhum agendamento ou comanda registrado desde o cadastro",
  },
};

export const PLAN_META = {
  DIAMOND: { label: "Diamond", color: "indigo" },
  PRO: { label: "Pro", color: "purple" },
  ESSENTIAL: { label: "Essential", color: "info" },
  FREE: { label: "Free", color: "light" },
};

export const getStatusMeta = (status) =>
  STATUS_META[status] || { label: status || "—", color: "gray", dot: "bg-slate-400" };

export const getHealthMeta = (health) =>
  HEALTH_META[health] || { label: health || "—", color: "gray", hint: "" };

export const getPlanMeta = (planId) =>
  PLAN_META[planId] || { label: planId || "—", color: "gray" };

export const formatDate = (value) =>
  value ? moment(value).format("DD/MM/YYYY") : "—";

export const formatDateTime = (value) =>
  value ? moment(value).format("DD/MM/YYYY HH:mm") : "—";

export const formatMoney = (value) => formatCurrencyBRL.format(Number(value || 0));

export const formatNumber = (value) =>
  Number(value || 0).toLocaleString("pt-BR");

export const formatCompactMoney = (value) => {
  const amount = Number(value || 0);

  if (Math.abs(amount) >= 1000) {
    return `R$ ${(amount / 1000).toLocaleString("pt-BR", {
      maximumFractionDigits: 1,
    })}k`;
  }

  return formatMoney(amount);
};

/** "Expira em 12 dias" / "Expirado há 3 dias" / "Expira hoje" */
export const describeExpiration = (daysToExpire) => {
  if (daysToExpire === null || daysToExpire === undefined) return "Sem data";
  if (daysToExpire === 0) return "Expira hoje";
  if (daysToExpire > 0) return `Expira em ${daysToExpire} dia${daysToExpire > 1 ? "s" : ""}`;

  const days = Math.abs(daysToExpire);
  return `Expirado há ${days} dia${days > 1 ? "s" : ""}`;
};

export const describeLastActivity = (lastActivityAt, daysSinceLastActivity) => {
  if (!lastActivityAt) return "Nunca usou";
  if (daysSinceLastActivity === 0) return "Ativo hoje";
  if (daysSinceLastActivity === 1) return "Ativo ontem";

  return `Há ${daysSinceLastActivity} dias`;
};

export const buildWhatsappLink = (phoneNumber) => {
  const digitsOnly = String(phoneNumber ?? "").replace(/\D/g, "");
  if (digitsOnly.length < 10) return "";

  const normalized = digitsOnly.startsWith("55") ? digitsOnly : `55${digitsOnly}`;

  return `https://wa.me/${normalized}`;
};

export const getInitials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

/** Cor determinística para o avatar, para o olho reconhecer a linha. */
export const getAvatarColor = (id) => {
  const palette = [
    "bg-rose-100 text-rose-700",
    "bg-sky-100 text-sky-700",
    "bg-amber-100 text-amber-700",
    "bg-emerald-100 text-emerald-700",
    "bg-violet-100 text-violet-700",
    "bg-cyan-100 text-cyan-700",
  ];

  return palette[Number(id || 0) % palette.length];
};

export const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Não foi possível concluir a operação.";
